import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@istari-frontend/ui";
import { ArrowLeft, ChevronLeft, ChevronRight, Plus, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type { ConversationStatus } from "~/ai-chat/model/conversation";
import {
  CONVERSATION_DOT_LABEL,
  conversationDotState,
  ConversationStatusDot,
} from "~/ai-chat/ui/conversation-status-dot";
import { useInlineTitleEdit } from "~/ai-chat/ui/use-inline-title-edit";
import { cn } from "~/utils/cn";

export interface ChatTab {
  // Stable client-side identity so empty (unsaved) chats persist as distinct tabs.
  key: string;
  // Null until the chat is named; the strip shows a placeholder instead.
  title: string | null;
  status: ConversationStatus | null;
  isActive: boolean;
  // Seen since its last update; a completed-but-unread chat shows the unread dot.
  isRead: boolean;
}

interface ChatTabsProps {
  tabs: ChatTab[];
  onSelect: (key: string) => void;
  onClose: (key: string) => void;
  onNew: () => void;
  // Enables double-click renaming; the conversation list's row menu stays the keyboard path.
  onRename?: (key: string, title: string | null) => void;
  onOpenHistory?: () => void;
}

// The open-chats strip: one tab per chat open this session (saved or a fresh unsaved
// one), a status dot, and a `+` to start another. Switching never stops a running
// chat (they stream in parallel), so several tabs can show the amber dot at once.
// When more tabs are open than fit, scroll arrows appear and off-screen tabs collapse
// into a `+N` overflow menu. The optional leading button opens the full chat history.
export function ChatTabs({ tabs, onSelect, onClose, onNew, onRename, onOpenHistory }: ChatTabsProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(() => new Set());

  const measure = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    const viewLeft = el.scrollLeft;
    const viewRight = el.scrollLeft + el.clientWidth;
    const hidden = new Set<string>();
    for (let i = 0; i < el.children.length; i++) {
      const child = el.children[i] as HTMLElement;
      const left = child.offsetLeft;
      const right = left + child.offsetWidth;
      // Off-screen if its box lies (nearly) entirely outside the scroll viewport.
      if (right <= viewLeft + 4 || left >= viewRight - 4) hidden.add(child.dataset.tabKey ?? "");
    }
    setHiddenKeys(hidden);
  };

  useEffect(() => {
    measure();
    const el = scrollRef.current;
    if (el === null) return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
    // Re-measure when the set of tabs changes.
  }, [tabs]);

  const scrollBy = (delta: number) => scrollRef.current?.scrollBy({ left: delta, behavior: "smooth" });
  const hiddenTabs = tabs.filter((tab) => hiddenKeys.has(tab.key));

  return (
    <div className="border-border flex h-11 shrink-0 items-center gap-1 border-b px-1">
      {onOpenHistory ? (
        <button type="button" onClick={onOpenHistory} aria-label="Show chat history" className={tabActionClassName}>
          <ArrowLeft className="size-4" />
        </button>
      ) : null}
      {canScrollLeft ? (
        <button
          type="button"
          onClick={() => scrollBy(-200)}
          aria-label="Scroll tabs left"
          className={tabActionClassName}
        >
          <ChevronLeft className="size-4" />
        </button>
      ) : null}
      <div
        ref={scrollRef}
        onScroll={measure}
        className="flex min-w-0 flex-1 [scrollbar-width:none] items-center overflow-x-auto [&::-webkit-scrollbar]:hidden"
      >
        {tabs.map((tab) => (
          <div
            key={tab.key}
            data-tab-key={tab.key}
            className={cn(
              "group/tab relative flex h-8 max-w-56 min-w-[100px] flex-1 basis-0 items-center gap-1.5 rounded-md px-3 text-sm font-medium transition-colors",
              // Browser-style separators: a hairline on the left of every tab except the first
              // and the ones touching the active pill, which carries its own edges.
              "before:bg-border before:absolute before:top-1/2 before:left-0 before:h-4 before:w-px before:-translate-y-1/2 first:before:hidden",
              tab.isActive
                ? "bg-sidebar-accent text-secondary-foreground before:hidden [&+*]:before:hidden"
                : "text-muted-foreground hover:bg-sidebar-accent/50 hover:text-foreground",
            )}
          >
            <ChatTabLabel tab={tab} onSelect={onSelect} onRename={onRename} />
            <button
              type="button"
              onClick={() => onClose(tab.key)}
              aria-label={`Close ${tab.title ?? NEW_CHAT_LABEL}`}
              className="hover:text-foreground shrink-0 rounded opacity-0 transition-opacity group-focus-within/tab:opacity-100 group-hover/tab:opacity-100 focus-visible:opacity-100"
            >
              <X className="size-3.5" />
            </button>
          </div>
        ))}
      </div>
      {canScrollRight ? (
        <button
          type="button"
          onClick={() => scrollBy(200)}
          aria-label="Scroll tabs right"
          className={tabActionClassName}
        >
          <ChevronRight className="size-4" />
        </button>
      ) : null}
      {hiddenTabs.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`${hiddenTabs.length} more chats`}
              className="bg-accent/50 text-muted-foreground hover:bg-accent hover:text-foreground shrink-0 rounded-md px-2 py-1.5 text-sm"
            >
              +{hiddenTabs.length}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-72 overflow-y-auto">
            {hiddenTabs.map((tab) => {
              const dotState = conversationDotState(tab.status, tab.isRead);
              const dotLabel = CONVERSATION_DOT_LABEL[dotState];
              return (
                <DropdownMenuItem key={tab.key} onSelect={() => onSelect(tab.key)}>
                  <ConversationStatusDot state={dotState} />
                  <span title={tab.title ?? undefined} className="truncate">
                    {tab.title ?? NEW_CHAT_LABEL}
                  </span>
                  {dotLabel ? <span className="sr-only">{dotLabel}</span> : null}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      <button type="button" onClick={onNew} aria-label="Start new chat" className={tabActionClassName}>
        <Plus className="size-4" />
      </button>
    </div>
  );
}

const NEW_CHAT_LABEL = "New chat";

const tabActionClassName =
  "text-muted-foreground hover:bg-sidebar-accent hover:text-foreground flex size-8 shrink-0 items-center justify-center rounded-md transition-colors";

interface ChatTabLabelProps {
  tab: ChatTab;
  onSelect: (key: string) => void;
  onRename?: (key: string, title: string | null) => void;
}

// The tab's select button, swapped for a title input while renaming. The input replaces the
// button rather than nesting in it: a button can't hold interactive content, and it would
// swallow the Space/Enter typed into the field.
function ChatTabLabel({ tab, onSelect, onRename }: ChatTabLabelProps) {
  const edit = useInlineTitleEdit(tab.title, (title) => onRename?.(tab.key, title));
  const label = tab.title ?? NEW_CHAT_LABEL;
  const dotState = conversationDotState(tab.status, tab.isRead);
  const dotLabel = CONVERSATION_DOT_LABEL[dotState];

  if (edit.isEditing) {
    return (
      <>
        <ConversationStatusDot state={dotState} />
        <input
          aria-label={`Rename ${label}`}
          autoFocus
          value={edit.draft ?? ""}
          onChange={(event) => edit.setDraft(event.target.value)}
          onBlur={edit.handleBlur}
          onKeyDown={edit.handleKeyDown}
          className="focus-visible:ring-ring/50 min-w-0 flex-1 rounded-sm bg-transparent outline-none focus-visible:ring-[3px]"
        />
      </>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onSelect(tab.key)}
      onDoubleClick={onRename ? edit.beginEdit : undefined}
      aria-current={tab.isActive ? "true" : undefined}
      className="flex min-w-0 flex-1 items-center gap-1.5 text-left outline-none"
    >
      <ConversationStatusDot state={dotState} />
      <span title={tab.title ?? undefined} className="min-w-0 flex-1 truncate">
        {label}
      </span>
      {dotLabel ? <span className="sr-only">{dotLabel}</span> : null}
    </button>
  );
}
